import { makeStyles, TextField, MenuItem, Select, FormControl, InputLabel, Button } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from "date-fns/locale/fr";

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1
  },
  selectType: {
    marginLeft: 5,
    marginRight: 5,
    width: 250
  },
  element: {
    marginLeft: 5,
    marginRight: 5
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column'
  }
}));

interface dateFilterElement {
  identifier: string;
  display: string;
};

const dateFilterTypes: dateFilterElement[] = [
  {identifier: 'lastx', display: 'Au cours des derniers'},
  {identifier: 'equals', display: 'Est égale à'},
  {identifier: 'between', display: 'Entre'},
  {identifier: 'after', display: 'Après'},
  {identifier: 'onOrAfter', display: 'Le ou après'},
  {identifier: 'before', display: 'Avant'},
  {identifier: 'onOrBefore', display: 'Le ou avant'}
];

interface Props {
  onFiltersUpdated: Function,
  name: string
};

const DateFilter = (props: Props) => {
  const classes = useStyles();
  const [dateFilterType, setDateFilterType] = useState<string>('');
  const [validNumber, setValidNumber] = useState<boolean>(true);
  const [days, setDays] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDate2, setSelectedDate2] = useState<Date | null>(null);

  const updateFilter = (suffix: string[], value: number[]) => {
    const toSend: any = {};

    if (suffix.length !== value.length)
      return;
    suffix.forEach((suff: string, i: number) => {
      toSend[props.name + suff] = value[i];
    });
    props.onFiltersUpdated(toSend)
  };

  useEffect(() => {
    if (!days && !selectedDate && !selectedDate2)
      return;
    const res = new Date(formatDate(selectedDate as Date));
    switch (dateFilterType) {
      case 'lastx':
        const now = formatDate(new Date());
        const resLastx = new Date(now.getTime());
        resLastx.setDate(resLastx.getDate() - ((+days) + 1));
        updateFilter(
          ['[lte]', '[gte]'],
          [now.getTime() / 1000, resLastx.getTime() / 1000]
        );
        break;
      case 'equals':
        const higherEquals = res;
        const lowerEquals = new Date (higherEquals);
        lowerEquals.setDate(higherEquals.getDate() - 1);
        updateFilter(
          ['[lte]', '[gte]'],
          [higherEquals.getTime() / 1000, lowerEquals.getTime() / 1000]
        );
        break;
      case 'between':
        if (!selectedDate || !selectedDate2)
          return;
        const higherBetween = new Date(formatDate(selectedDate2 as Date));
        const lowerBetween = res;
        lowerBetween.setDate(lowerBetween.getDate() - 1);
        updateFilter(
          ['[lte]', '[gte]'],
          [higherBetween.getTime() / 1000, lowerBetween.getTime() / 1000]
        );
        break;
      case 'after':
        const after = res;
        after.setDate(after.getDate());
        updateFilter(['[gt]'], [after.getTime() / 1000]);
        break;
      case 'onOrAfter':
        const onOrAfter = res;
        onOrAfter.setDate(onOrAfter.getDate() - 1);
        updateFilter(['[gte]'], [onOrAfter.getTime() / 1000]);
        break;
      case 'before':
        const before = res;
        before.setDate(before.getDate() - 1);
        updateFilter(['[lt]'], [before.getTime() / 1000]);
        break;
      case 'onOrBefore':
        const onOrBefore = res;
        updateFilter(['[lte]'], [onOrBefore.getTime() / 1000]);
        break;
    }
    // eslint-disable-nest-line
  }, [days, selectedDate, selectedDate2]);

  const reset = () => {
    setValidNumber(true);
    setDays('');
    setSelectedDate(null);
    setSelectedDate2(null);
  };

  const resetAll = () => {
    reset();
    props.onFiltersUpdated({});
    setDateFilterType('');
  };

  const handleDateFilterTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setDateFilterType(event.target.value as string);
    reset();
  };

  const handleNumberChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const nb = Number(event.target.value);
    setValidNumber((!isNaN(nb) && nb > 0) || event.target.value === '');
    setDays(event.target.value as string);
  }

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleDateChange2 = (date: Date | null) => {
    setSelectedDate2(date);
  };

  const formatDate = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setHours(24);
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
  };

  return (
    <div className={classes.container}>
      <FormControl variant="outlined" className={classes.selectType}>
        <InputLabel id="dateSelectFilter">Date</InputLabel>
        <Select
          labelId="dateFilterSelectLabel"
          id="dateFilterSelect"
          value={dateFilterType}
          onChange={handleDateFilterTypeChange}
          label="Date"
        >
          {dateFilterTypes.map((currentType, i) =>
            <MenuItem key={i} value={currentType.identifier}>{currentType.display}</MenuItem>
          )}
        </Select>
      </FormControl>
      {dateFilterType === 'lastx' &&
        <TextField
          error={!validNumber}
          label="Jours"
          type="number"
          value={days}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={handleNumberChange}
          className={classes.element}
        />
      }
      { ['equals', 'between', 'after', 'onOrAfter', 'before', 'onOrBefore'].includes(dateFilterType) &&
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
          <KeyboardDatePicker
            className={classes.element}
            style={{marginTop: 0}}
            disableToolbar
            variant="inline"
            format="dd/MM/yyyy"
            label="Date"
            value={selectedDate}
            onChange={handleDateChange}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
          />
        </MuiPickersUtilsProvider>
      }
      { ['between'].includes(dateFilterType) &&
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
        <KeyboardDatePicker
          className={classes.element}
          style={{marginTop: 0}}
          disableToolbar
          variant="inline"
          format="dd/MM/yyyy"
          label="Seconde date"
          value={selectedDate2}
          onChange={handleDateChange2}
          KeyboardButtonProps={{
            'aria-label': 'change date',
          }}
        />
      </MuiPickersUtilsProvider>
      }
      {dateFilterType !== '' &&
        <Button color="secondary" onClick={resetAll}>
          Reset
        </Button>
      }
    </div>
  );
};

export default DateFilter